import { displayDigits } from '../../utils/displayDigits';
import React, { useState } from 'react';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import type { CalendarHealthDocument } from '../../services/api';
import { HealthDocumentDetailSheet } from '../health/HealthDocumentDetailSheet';
import { useLongPress } from '../../hooks/useLongPress';

interface Props {
  doc: CalendarHealthDocument;
  onLongPress?: () => void;
  onDeleted?: () => void;
  dragProps?: {
    draggable?: boolean;
    onDragStart?: (e: React.DragEvent) => void;
    'data-dragging'?: string;
  };
}

export function CalendarHealthDocumentRow({ doc, onLongPress, onDeleted, dragProps }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { bind, wrapClick } = useLongPress(() => onLongPress?.());

  const typeLabel = t(`calendar.healthDocument.testType.${doc.test_type ?? 'unknown'}`, {
    defaultValue: doc.test_type ?? t('calendar.healthDocument.testType.unknown', 'Health report'),
  });

  const failed = doc.status === 'failed';

  return (
    <>
      <div className="calendar-health-doc-row-wrap">
        <button
          type="button"
          className={`calendar-health-doc-row${failed ? ' calendar-health-doc-row--failed' : ''}${dragProps?.['data-dragging'] === 'true' ? ' calendar-health-doc-row--dragging' : ''}`}
          onClick={wrapClick(() => setOpen(true))}
          {...(onLongPress ? bind : {})}
          {...dragProps}
        >
          <span className="calendar-health-doc-row__icon" aria-hidden>
            {failed ? '⚠️' : <IoDocumentTextOutline size={22} />}
          </span>
          <span className="calendar-health-doc-row__text">
            <strong>{typeLabel}</strong>
            <span className="calendar-health-doc-row__filename" dir="auto" title={doc.original_filename}>
              {displayDigits(doc.original_filename)}
            </span>
            {failed || doc.saved_metrics_count > 0 ? <span className="calendar-health-doc-row__status">
              {failed ? t('calendar.healthDocument.failed', 'Analysis failed') : displayDigits(t('calendar.healthDocument.metricsSaved', { count: doc.saved_metrics_count }))}
            </span> : null}
          </span>
        </button>
        {onLongPress ? (
          <button
            type="button"
            className="calendar-health-doc-row__menu"
            aria-label={t('calendar.actions.more', 'More actions')}
            onClick={(e) => {
              e.stopPropagation();
              onLongPress();
            }}
          >
            ⋮
          </button>
        ) : null}
      </div>

      <HealthDocumentDetailSheet
        doc={doc}
        open={open}
        onClose={() => setOpen(false)}
        onDeleted={onDeleted}
      />
    </>
  );
}
